import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { COMMON_NAMESPACE } from '@/i18n';
import '@/components/OnboardingProgress/OnboardingProgress.sass';

type OnboardingProgressProps = {
  currentStep: number;
  totalSteps?: number;
};

function getSegmentStatus(
  stepNumber: number,
  currentStep: number,
): 'active' | 'pending' {
  return stepNumber > currentStep ? 'pending' : 'active';
}

export function OnboardingProgress({
  currentStep,
  totalSteps = 4,
}: OnboardingProgressProps): JSX.Element {
  const { t } = useTranslation(COMMON_NAMESPACE);

  if (currentStep < 1 || currentStep > totalSteps) {
    throw new Error(
      `currentStep must be between 1 and ${totalSteps}, received ${currentStep}`,
    );
  }

  return (
    <Box
      role="progressbar"
      className="onboarding-progress"
      aria-valuenow={currentStep}
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      aria-label={t('onboardingStepAriaLabel', {
        current: currentStep,
        total: totalSteps,
      })}
    >
      {Array.from({ length: totalSteps }, (_, index) => {
        const stepNumber = index + 1;
        const status = getSegmentStatus(stepNumber, currentStep);

        return (
          <Box
            key={stepNumber}
            className="onboarding-progress__segment"
            data-testid={`progress-segment-${stepNumber}`}
            data-status={status}
            aria-hidden
          />
        );
      })}
    </Box>
  );
}
